import React, { useState } from "react";
import {
  Box,
  Typography,
  Link,
  Grid,
  Paper,
  Modal,
  IconButton,
  CircularProgress,
  Autocomplete,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";

const FACULTY_OPTIONS = [
  "Architecture & Planning",
  "Business Management",
  "Computing & IT",
  "Education",
  "Engineering & Technology",
  "Fashion, Textile & Media Studies",
  "Health & Medical Sciences",
  "LAW",
  "Life Sciences",
  "Science & Technology",
  "Social Sciences",
  "Misc.",
  "Other",
];

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");

// Prominent flag shown to the Reviewer and Final Approver when the applicant
// has a low GPA/CGPA warning on this application or on their record.
const LowGpaWarningBanner = ({ data = {} }) => {
  const flag = data.gpaFlag || {};
  const warning = data.gpaWarning || null;
  const activeWarning = warning && warning.state && warning.state !== "cleared";
  const flagged = (flag.level && flag.level !== "none") || activeWarning;
  if (!flagged) return null;

  const prior = flag.priorWarning || warning?.firstWarning || null;
  const override = flag.override && flag.override.at ? flag.override : null;
  const pendingOverride =
    warning?.pendingOverride && warning.pendingOverride.at && warning.pendingOverride.used === false
      ? warning.pendingOverride
      : null;

  let headline = "Low GPA/CGPA on record";
  if (flag.level === "warning") headline = "First low GPA/CGPA warning — acknowledged by student";
  else if (flag.level === "blocked_overridden") headline = "Second low GPA/CGPA — override granted by Final Approver";
  else if (warning?.state === "blocked") headline = "Student is blocked for repeated low GPA/CGPA";
  else if (warning?.state === "warned") headline = "Student has an active low GPA/CGPA warning";

  return (
    <Box
      sx={{
        mt: 1,
        mb: 2,
        p: 2,
        borderRadius: 1,
        border: "2px solid",
        borderColor: "error.main",
        bgcolor: "#fdecea",
      }}
    >
      <Typography sx={{ fontWeight: "bold", color: "error.main", fontSize: 15 }}>
        ⚠ LOW GPA/CGPA WARNING
      </Typography>
      <Typography sx={{ color: "error.main", mb: 0.5 }}>{headline}</Typography>
      <Typography variant="body2">
        This application's GPA/CGPA: <strong>{data.classOfLastInstitution ?? "—"}</strong>
      </Typography>

      {prior && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Previous warning — Application #{prior.applicationNumber ?? "—"}, GPA/CGPA{" "}
          <strong>{prior.gpa ?? "—"}</strong>
          {prior.semester != null ? `, semester ${prior.semester}` : ""}, issued{" "}
          {fmtDate(prior.issuedAt || prior.at)}
        </Typography>
      )}

      {override && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Override by <strong>{override.grantedByName || "—"}</strong>
          {override.grantedByRole ? ` (${override.grantedByRole})` : ""} on {fmtDate(override.at)}
          {override.reason ? ` — reason: ${override.reason}` : ""}
        </Typography>
      )}

      {!override && pendingOverride && (
        <Typography variant="body2" sx={{ mt: 0.5, fontStyle: "italic" }}>
          Override pending (granted by {pendingOverride.grantedByName || "—"} on {fmtDate(pendingOverride.at)}) — not yet used.
        </Typography>
      )}
    </Box>
  );
};

const ApplicationView = ({ data = {}, editable, applicationId, onSave, universities = [], editApprovedFee = false, hidePrivateSections = false }) => {
  const [open, setOpen] = useState(false);
  const [docUrl, setDocUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const handleOpen = (url) => {
    setDocUrl(url);
    setLoading(true);
    setOpen(true);
  };

  const handleClose = () => {
    setDocUrl("");
    setOpen(false);
    setLoading(false);
  };

  const openEdit = () => {
    setForm({
      faculty: data.faculty || "",
      otherFaculty: data.otherFaculty || "",
      degree: data.degree || "",
      grade: data.grade ?? "",
      institutionName: data.institutionName || "",
      otherInstitutionName: data.otherInstitutionName || "",
      institutionAddress: data.institutionAddress || "",
      tuttionFee: data.tuttionFee ?? "",
      classOfLastInstitution: data.classOfLastInstitution ?? "",
      enrollmentNo: data.enrollmentNo || "",
      approvedFee: data.approvedFee ?? "",
    });
    setEditOpen(true);
  };

  const handleSave = async () => {
    if (typeof onSave !== "function") return;
    setSaving(true);
    try {
      await onSave(form);
      setEditOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const setField = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  // Reusable value renderer
  const renderValue = (value, isLink) => {
    if (isLink) {
      const url = typeof value === "object" ? value?.url : value;
      if (!url) {
        return (
          <Paper
            variant="outlined"
            sx={{
              p: 1,
              bgcolor: "#f8f8f8",
              fontSize: 14,
              borderRadius: 1,
              borderColor: "green",
              fontStyle: "italic",
              color: "text.secondary",
            }}
          >
            No document attached
          </Paper>
        );
      }
      const label = typeof value === "object" ? value.fileName || url : url;
      return (
        <Paper
          variant="outlined"
          sx={{
            p: 1,
            bgcolor: "#f8f8f8",
            borderRadius: 1,
            borderColor: "green",
          }}
        >
          <Link
            component="button"
            onClick={() => handleOpen(url)}
            sx={{
              color: "green",
              textDecoration: "underline",
              fontSize: 14,
              cursor: "pointer",
            }}
          >
            {label}
          </Link>
        </Paper>
      );
    }

    return (
      <Paper
        variant="outlined"
        sx={{
          p: 1,
          bgcolor: "#f8f8f8",
          fontSize: 14,
          borderRadius: 1,
          borderColor: "green",
        }}
      >
        {value || value === 0 ? value : "-"}
      </Paper>
    );
  };

  const FormRow = ({ label1, value1, label2, value2, isLink, isLink1, isLink2 }) => (
    <Grid container spacing={2} sx={{ mb: 1 }}>
      <Grid item xs={12} sm={6}>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", mb: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
            {label1}
          </Typography>
        </Box>
        {renderValue(value1, isLink1 !== undefined ? isLink1 : isLink)}
      </Grid>
      {label2 && (
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.5 }}>
            {label2}
          </Typography>
          {renderValue(value2, isLink2 !== undefined ? isLink2 : isLink)}
        </Grid>
      )}
    </Grid>
  );

  if (!data || Object.keys(data).length === 0) {
    return (
      <Typography variant="body1" sx={{ p: 2 }}>
        No data available.
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        p: 4,
        fontFamily: "Helvetica, Arial, sans-serif",
        position: "relative",
      }}
    >
      {/* Orphan / Returning badges — hidden for partners */}
      {!hidePrivateSections && (
      <Box sx={{ position: "absolute", top: 16, right: 24, display: "flex", gap: 1 }}>
        <Box
          sx={{
            bgcolor:
              data.orphan === "yes"
                ? "success.main"
                : data.orphan === "no"
                ? "grey.600"
                : "warning.main",
            color: "#fff",
            px: 2,
            py: 0.5,
            borderRadius: 1,
            fontWeight: "bold",
            fontSize: 12,
          }}
        >
          Orphan: {data.orphan === "yes" ? "Yes" : data.orphan === "no" ? "No" : "Not specified"}
        </Box>
        {data.isReturning && (
          <Box
            sx={{
              bgcolor: "success.main",
              color: "#fff",
              px: 2,
              py: 0.5,
              borderRadius: 1,
              fontWeight: "bold",
              fontSize: 12,
            }}
          >
            Returning Student
          </Box>
        )}
      </Box>
      )}

      <LowGpaWarningBanner data={data} />

      {/* Application Info */}
      <Typography
        variant="h6"
        sx={{
          mt: 3,
          mb: 2,
          fontWeight: "bold",
          color: "green",
          borderBottom: "2px solid green",
          pb: 0.5,
        }}
      >
        Application Info
      </Typography>
      <FormRow
        label1="Application Number"
        value1={data.applicationNumber}
        label2="Application Status"
        value2={data.status}
      />
      <FormRow
        label1="Tuition Fee"
        value1={data.tuttionFee}
        label2="Submission Date"
        value2={data.applicationSubmitDate}
      />

      {/* Notes */}
      <Typography
        variant="h6"
        sx={{
          mt: 3,
          mb: 2,
          fontWeight: "bold",
          color: "green",
          borderBottom: "2px solid green",
          pb: 0.5,
        }}
      >
        Notes
      </Typography>
      {data.notes?.length ? (
        data.notes.map((item, index) => (
          <Typography key={index} sx={{ mb: 0.5 }}>
            {index + 1}. {item.remarks}
          </Typography>
        ))
      ) : (
        <Typography>No notes available</Typography>
      )}

      {/* Personal Information */}
      <Typography
        variant="h6"
        sx={{
          mt: 3,
          mb: 2,
          fontWeight: "bold",
          color: "green",
          borderBottom: "2px solid green",
          pb: 0.5,
        }}
      >
        Personal Information
      </Typography>
      <FormRow
        label1="First Name"
        value1={data.firstName}
        label2="Last Name"
        value2={data.lastName}
      />
      <FormRow label1="Email" value1={data.email} label2="Gender" value2={data.gender} />
      <FormRow label1="Address" value1={data.address} label2="Town" value2={data.town} />
      <FormRow
        label1="Mobile Phone"
        value1={data.phoneNo_Mobile}
        label2="Home Phone"
        value2={data.phoneNo_Home}
      />
      <FormRow label1="CNIC No" value1={data.CNICNo} label2="Country" value2={data.country} />

      {/* Parents — hidden for partners */}
      {!hidePrivateSections && (
      <>
      <Typography
        variant="h6"
        sx={{
          mt: 3,
          mb: 2,
          fontWeight: "bold",
          color: "green",
          borderBottom: "2px solid green",
          pb: 0.5,
        }}
      >
        Parents
      </Typography>
      <FormRow
        label1="Father Name"
        value1={data.fatherName}
        label2="Mother Name"
        value2={data.motherName}
      />
      <FormRow
        label1="Gross Household Income"
        value1={data.presentHouseholdIncome}
        label2="Family Members"
        value2={data.totalFamilyMembers}
      />
      </>
      )}

      {/* Education */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mt: 3,
          mb: 2,
          borderBottom: "2px solid green",
          pb: 0.5,
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", color: "green", flex: 1 }}
        >
          Education
        </Typography>
        {editable && applicationId && (
          <IconButton size="small" onClick={openEdit} sx={{ color: "green" }}>
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <FormRow
        label1="Degree"
        value1={data.degree}
        label2="Grade"
        value2={data.grade}
      />
      <FormRow
        label1="Faculty"
        value1={data.faculty}
        label2="Other Faculty"
        value2={data.otherFaculty}
      />
      <FormRow
        label1="Institution"
        value1={data.institutionName}
        label2="Other Institution"
        value2={data.otherInstitutionName}
      />
      <FormRow
        label1="Institution Address"
        value1={data.institutionAddress}
        label2="Tuition Fee"
        value2={data.tuttionFee}
      />
      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.5 }}>
            GPA/CGPA of previous semester
          </Typography>
          {renderValue(data.classOfLastInstitution)}
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.5 }}>
            Enrollment #
          </Typography>
          {renderValue(data.enrollmentNo)}
        </Grid>
      </Grid>
      <FormRow
        label1="Exam Result"
        value1={data.resultOfLastExamFile}
        label2="Fee Voucher"
        value2={data.feeVoucherFile}
        isLink
      />

      {/* References & Documents — hidden for partners */}
      {!hidePrivateSections && (
      <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mt: 3,
          mb: 2,
          borderBottom: "2px solid green",
          pb: 0.5,
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", color: "green", flex: 1 }}
        >
          References
        </Typography>
      </Box>
      <FormRow
        label1="Ref 1 Name"
        value1={data.referenceName1}
        label2="Ref 1 Email"
        value2={data.referenceEmail1}
      />
      <FormRow
        label1="Ref 1 Phone"
        value1={data.referencePhone1}
        label2="Ref Letter 1"
        value2={data.referenceLetterFile1}
        isLink1={false}
        isLink2
      />
      <FormRow
        label1="Ref 2 Name"
        value1={data.referenceName2}
        label2="Ref 2 Phone"
        value2={data.referencePhone2}
        isLink1={false}
        isLink2={false}
      />
      <FormRow
        label1="Ref 2 Email"
        value1={data.referenceEmail2}
        label2="Ref Letter 2"
        value2={data.referenceLetterFile2}
        isLink1={false}
        isLink2
      />

      {/* Documents */}
      <Typography
        variant="h6"
        sx={{
          mt: 3,
          mb: 2,
          fontWeight: "bold",
          color: "green",
          borderBottom: "2px solid green",
          pb: 0.5,
        }}
      >
        Documents
      </Typography>
      <FormRow
        label1="CNIC Front"
        value1={data.CNICFrontFile}
        label2="CNIC Back"
        value2={data.CNICBackFile}
        isLink
      />
      <FormRow
        label1="Parent CNIC Front"
        value1={data.parentCNICFrontFile}
        label2="Parent CNIC Back"
        value2={data.parentCNICBackFile}
        isLink
      />
      <FormRow
        label1="Income Proof"
        value1={data.proofOfIncomeFile}
        label2="Electric Bill"
        value2={data.currentElectricBillFile}
        isLink
      />
      <FormRow
        label1="Matric and Intermediate Marksheet"
        value1={data.additionalDocs}
        label2="Other Documents"
        value2={data.otherDocumentsFile}
        isLink
      />
      </>
      )}

      {/* Edit Application Dialog */}
      <Dialog open={editOpen} onClose={() => !saving && setEditOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Edit Application</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={FACULTY_OPTIONS}
                value={form.faculty || null}
                onChange={(e, newValue) =>
                  setForm((prev) => ({
                    ...prev,
                    faculty: newValue || "",
                    otherFaculty: newValue !== "Other" ? "" : prev.otherFaculty,
                  }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Faculty" variant="outlined" size="small" />
                )}
              />
            </Grid>
            {form.faculty === "Other" && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Other Faculty"
                  variant="outlined"
                  size="small"
                  value={form.otherFaculty}
                  onChange={setField("otherFaculty")}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Degree"
                variant="outlined"
                size="small"
                value={form.degree}
                onChange={setField("degree")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Semester"
                variant="outlined"
                size="small"
                type="number"
                value={form.grade}
                onChange={setField("grade")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={universities}
                value={form.institutionName || null}
                onChange={(e, newValue) =>
                  setForm((prev) => ({
                    ...prev,
                    institutionName: newValue || "",
                    otherInstitutionName: newValue !== "Other" ? "" : prev.otherInstitutionName,
                  }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Institution Name" variant="outlined" size="small" />
                )}
              />
            </Grid>
            {form.institutionName === "Other" && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Other Institution Name"
                  variant="outlined"
                  size="small"
                  value={form.otherInstitutionName}
                  onChange={setField("otherInstitutionName")}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Institution Address"
                variant="outlined"
                size="small"
                value={form.institutionAddress}
                onChange={setField("institutionAddress")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tuition Fee"
                variant="outlined"
                size="small"
                type="number"
                value={form.tuttionFee}
                onChange={setField("tuttionFee")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="GPA/CGPA"
                variant="outlined"
                size="small"
                type="number"
                value={form.classOfLastInstitution}
                onChange={setField("classOfLastInstitution")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Enrollment #"
                variant="outlined"
                size="small"
                value={form.enrollmentNo}
                onChange={setField("enrollmentNo")}
              />
            </Grid>
            {editApprovedFee && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Approved Fee"
                  variant="outlined"
                  size="small"
                  type="number"
                  value={form.approvedFee}
                  onChange={setField("approvedFee")}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ bgcolor: "green", "&:hover": { bgcolor: "darkgreen" } }}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal for document preview */}
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 2,
            width: "80%",
            height: "80%",
            borderRadius: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "#fff",
              bgcolor: "red",
              "&:hover": { bgcolor: "darkred" },
            }}
          >
            <CloseIcon />
          </IconButton>

          {loading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(255,255,255,0.8)",
                zIndex: 10,
              }}
            >
              <CircularProgress sx={{ color: "green" }} size={60} />
            </Box>
          )}

          <iframe
            src={docUrl}
            title="Document Preview"
            width="100%"
            height="100%"
            style={{ border: "none" }}
            onLoad={() => setLoading(false)}
          />
        </Box>
      </Modal>
    </Box>
  );
};

export default ApplicationView;
